<template>
  <div class="page-wrap">
    <PageHeader
      title="계정 수동 발급"
    />

    <FormCard>
      <template #header>
        <div class="card-header-row">
          <span>신청 정보 입력</span>
          <div class="card-header-actions">
            <v-btn
              variant="flat"
              color="primary"
              size="small"
              :loading="submitting"
              :disabled="submitting"
              @click="onSubmit"
            >
              발급
            </v-btn>
            <v-btn
              variant="outlined"
              size="small"
              :disabled="submitting"
              @click="onClear"
            >
              초기화
            </v-btn>
          </div>
        </div>
      </template>

      <div class="form-grid">
        <div class="field-group">
          <label class="field-label">
            학교명 <span class="field-required">(필수)</span>
          </label>
          <v-text-field
            v-model="form.schoolName"
            placeholder="예: 한강초등학교"
            density="compact"
            variant="outlined"
            hide-details
          />
        </div>

        <div class="field-group">
          <label class="field-label">교육청</label>
          <v-select
            v-model="form.office"
            :items="officeOptions"
            density="compact"
            variant="outlined"
            placeholder="필요 시 선택"
            clearable
            hide-details
          />
        </div>

        <div class="field-group">
          <label class="field-label">
            담당 교사 이름 <span class="field-required">(필수)</span>
          </label>
          <v-text-field
            v-model="form.name"
            placeholder="예: 김선생"
            density="compact"
            variant="outlined"
            hide-details
          />
        </div>

        <div class="field-group">
          <label class="field-label">
            이메일 <span class="field-required">(필수)</span>
          </label>
          <v-text-field
            v-model="form.email"
            placeholder="예: teacher@school.kr"
            density="compact"
            variant="outlined"
            type="email"
            hide-details
          />
        </div>

        <div class="field-group">
          <label class="field-label">
            전화번호
          </label>
          <v-text-field
            v-model="form.phonenum"
            placeholder="예: 010-1234-5678"
            density="compact"
            variant="outlined"
            hide-details
          />
        </div>

        <div class="field-group">
          <label class="field-label">
            학년 <span class="field-required">(필수)</span>
          </label>
          <v-select
            v-model="form.grade"
            :items="gradeOptions"
            density="compact"
            variant="outlined"
            placeholder="학년 선택"
            hide-details
          />
        </div>

        <div class="field-group">
          <label class="field-label">
            영어 출판사 <span v-if="!isLowerGrade" class="field-required">(필수)</span>
          </label>
          <v-select
            v-model="form.publisher"
            :items="englishPublisherItems"
            item-title="label"
            item-value="value"
            :disabled="isLowerGrade"
            density="compact"
            variant="outlined"
            :placeholder="isLowerGrade ? '1, 2학년은 영어 출판사 없음' : '출판사 선택'"
            hide-details
          />
        </div>

        <div class="field-group">
          <label class="field-label">
            수학 출판사 <span v-if="isLowerGrade" class="field-required">(필수)</span>
          </label>
          <v-select
            v-model="form.mathPublisher"
            :items="mathPublisherItems"
            density="compact"
            variant="outlined"
            placeholder="출판사 선택"
            hide-details
          />
        </div>

        <div class="field-group field-group--pair">
          <div class="field-group">
            <label class="field-label">
              체험 시작일 <span class="field-required">(필수)</span>
            </label>
            <v-text-field
              v-model="startDateIso"
              type="date"
              density="compact"
              variant="outlined"
              hide-details
            />
          </div>
          <div class="field-group">
            <label class="field-label">체험 종료일</label>
            <v-text-field
              v-model="endDateIso"
              type="date"
              density="compact"
              variant="outlined"
              hide-details
            />
          </div>
        </div>

        <div class="field-group field-group--pair">
          <div class="field-group">
            <label class="field-label">분류</label>
            <v-select
              v-model="form.type"
              :items="['-', '무료', '연수', '서포터즈', 'AI미래교육연구회', '박람회']"
              density="compact"
              variant="outlined"
              hide-details
            />
          </div>
          <div class="field-group">
            <label class="field-label">발급 방식</label>
            <v-select
              v-model="form.issueMode"
              :items="issueModeOptions"
              item-title="label"
              item-value="value"
              density="compact"
              variant="outlined"
              hide-details
            />
          </div>
        </div>

        <div v-if="form.issueMode !== 'default'" class="field-group">
          <label class="field-label">
            학급 수 <span class="field-required">(필수)</span>
          </label>
          <v-text-field
            v-model.number="form.classCnt"
            type="number"
            min="1"
            :max="maxClassCnt"
            density="compact"
            variant="outlined"
            hide-details
          />
        </div>

        <div v-if="form.issueMode === 'oneToN'" class="field-group">
          <label class="field-label">
            반당 학생 수 <span class="field-required">(필수)</span>
          </label>
          <v-text-field
            v-model.number="form.studentCnt"
            type="number"
            min="1"
            max="70"
            density="compact"
            variant="outlined"
            hide-details
          />
        </div>
      </div>

      <p v-if="form.issueMode !== 'default'" class="issue-summary">
        <v-icon icon="mdi-information-outline" size="14" />
        {{ issueSummaryText }}
      </p>

      <p v-if="errorMsg" class="form-error">
        <v-icon icon="mdi-alert-circle-outline" size="14" /> {{ errorMsg }}
      </p>
    </FormCard>

    <!-- 발급 확인 모달 -->
    <v-dialog
      v-model="showConfirm"
      max-width="480"
      persistent
      class="modal-root"
    >
      <div class="modal-card">
        <header class="modal-card__header">
          <span class="modal-card__title">
            <v-icon icon="mdi-help-circle-outline" size="20" class="modal-card__title-icon" />
            무료체험 계정 발급 확인
          </span>
        </header>
        <div class="modal-card__body">
          <p class="modal-card__lead">아래 정보로 발급할까요?</p>
          <dl class="modal-card__list">
            <div class="modal-card__row"><dt>학교명</dt><dd>{{ form.schoolName }}</dd></div>
            <div v-if="form.office" class="modal-card__row"><dt>교육청</dt><dd>{{ form.office }}</dd></div>
            <div class="modal-card__row"><dt>담당 교사</dt><dd>{{ form.name }}</dd></div>
            <div class="modal-card__row"><dt>이메일</dt><dd>{{ form.email }}</dd></div>
            <div v-if="form.phonenum" class="modal-card__row"><dt>전화번호</dt><dd>{{ form.phonenum }}</dd></div>
            <div class="modal-card__row"><dt>학년</dt><dd>{{ form.grade }}</dd></div>
            <div v-if="!isLowerGrade" class="modal-card__row"><dt>영어 출판사</dt><dd>{{ form.publisher }}</dd></div>
            <div v-if="form.mathPublisher" class="modal-card__row"><dt>수학 출판사</dt><dd>{{ form.mathPublisher }}</dd></div>
            <div class="modal-card__row"><dt>체험 시작일</dt><dd>{{ form.startDate }}</dd></div>
            <div v-if="form.endDate" class="modal-card__row"><dt>체험 종료일</dt><dd>{{ form.endDate }}</dd></div>
            <div class="modal-card__row"><dt>분류</dt><dd>{{ form.type }}</dd></div>
            <div class="modal-card__row"><dt>발급 방식</dt><dd>{{ issueSummaryText }}</dd></div>
          </dl>
        </div>
        <footer class="modal-card__footer">
          <button
            class="modal-btn modal-btn--primary"
            :disabled="submitting"
            @click="onConfirmIssue"
          >
            <v-icon :icon="submitting ? 'mdi-loading' : 'mdi-check'" :class="{ spin: submitting }" size="16" />
            {{ submitting ? '발급 중...' : '발급' }}
          </button>
          <button
            class="modal-btn modal-btn--secondary"
            :disabled="submitting"
            @click="showConfirm = false"
          >
            취소
          </button>
        </footer>
      </div>
    </v-dialog>

    <!-- 결과 모달 (성공/실패) -->
    <v-dialog
      v-model="showResult"
      max-width="480"
      class="modal-root"
    >
      <div class="modal-card">
        <header class="modal-card__header">
          <span class="modal-card__title">
            <v-icon
              :icon="resultStatus === 'ok' ? 'mdi-check-circle' : 'mdi-alert-circle'"
              size="20"
              :class="['modal-card__title-icon', resultStatus === 'ok' ? 'is-ok' : 'is-err']"
            />
            {{ resultStatus === 'ok' ? '발급 완료' : '발급 실패' }}
          </span>
        </header>
        <div class="modal-card__body">
          <p class="modal-card__lead" :class="resultStatus === 'ok' ? 'is-ok' : 'is-err'">
            {{ resultMessage }}
          </p>
          <template v-if="resultStatus === 'ok' && resultMeta">
            <dl class="modal-card__list">
              <div v-if="resultMeta.schoolCode" class="modal-card__row">
                <dt>학교 코드</dt><dd>{{ resultMeta.schoolCode }}</dd>
              </div>
              <div class="modal-card__row"><dt>메일</dt><dd>1건 전송</dd></div>
              <div class="modal-card__row"><dt>문자</dt><dd>1건 전송</dd></div>
              <template v-if="resultMeta.classes?.length">
                <div
                  v-for="cls in resultMeta.classes"
                  :key="cls.classNum"
                  class="modal-card__row"
                >
                  <dt>{{ cls.classNum }}반</dt>
                  <dd>
                    교사 <b>{{ cls.teacherId }}</b> · LMS <b>{{ cls.lmsId }}</b> ·
                    학생 <b>{{ studentRangeText(cls) }}</b>
                  </dd>
                </div>
              </template>
            </dl>
          </template>
        </div>
        <footer class="modal-card__footer">
          <button
            class="modal-btn modal-btn--primary"
            @click="showResult = false"
          >
            닫기
          </button>
        </footer>
      </div>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, watch, computed } from "vue";
import { useStore } from "vuex";

import PageHeader from "@/components/ui/PageHeader.vue";
import FormCard from "@/components/ui/FormCard.vue";

const store = useStore();

const gradeOptions = ["1학년", "2학년", "3학년", "4학년", "5학년", "6학년"];
const publisherOptions = [
  { label: "천재교과서 (함순애)", value: "천재교과서(함순애)" },
  { label: "천재교과서 (이동환)", value: "천재교과서(이동환)" },
  { label: "천재교과서 (김태은)", value: "천재교과서(김태은)" },
  { label: "미래엔", value: "미래엔 교과서" },
  { label: "YBM (최희경)", value: "YBM 교과서 (최희경)" },
  { label: "YBM (김혜리)", value: "YBM 교과서 (김혜리)" },
  { label: "동아", value: "동아교과서" },
  { label: "아이스크림", value: "아이스크림교과서" },
  { label: "대교", value: "대교교과서" },
];

// 1, 2학년은 영어 없이 수학만 — 백엔드 호환을 위해 publisher 는 기존처럼 "수학(M)"으로 전송
const MATH_PUBLISHER_VALUE = "수학(M)";
// 수학 출판사 목록 — 천재교과서는 1, 2학년 전용(1, 2학년은 이것만 선택 가능)
const MATH_LOWER_GRADE_PUBLISHER = "천재교과서";
const mathPublisherOptions = [
  MATH_LOWER_GRADE_PUBLISHER,
  "천재출판사(한대희)",
  "천재출판사(박만구)",
  "지학사출판사",
  "비상교육출판사",
  "동아출판사",
  "미래엔출판사",
  "아이스크림출판사",
  "YBM출판사",
  "디딤돌출판사",
];

// 백엔드 data/title.json 에 5·6학년 단원만 있는 영어 출판사 — 3·4학년과 조합하면 발급이 실패한다.
const GRADE_LIMITED_PUBLISHERS = {
  "대교교과서": { label: "대교", grades: [5, 6] },
  "비상교과서": { label: "비상", grades: [5, 6] },
};

// 교육청 목록 (가나다순) — 학교 등록(계정 발급) 화면과 동일한 목록
const officeOptions = [
  "강원도교육청",
  "경기도교육청",
  "경상남도교육청",
  "경상북도교육청",
  "광주광역시교육청",
  "대구광역시교육청",
  "대전광역시교육청",
  "베트남교육청",
  "부산광역시교육청",
  "서울특별시교육청",
  "세종특별자치시교육청",
  "울산광역시교육청",
  "인천광역시교육청",
  "전라남도교육청",
  "전라북도교육청",
  "제주특별자치도교육청",
  "충청남도교육청",
  "충청북도교육청",
];

const pad2 = (n) => String(n).padStart(2, "0");
const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};
const isoToDisplay = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${y}. ${m}. ${d}`;
};

// 발급 방식 — default: 기존과 동일(1반·학생 5명) / oneToN: 학급 수 × 반당 학생 수 / oneToOne: 학급마다 학생 1명
const issueModeOptions = [
  { label: "기본 (1반 · 학생 5명)", value: "default" },
  { label: "1:N — 학급 수 × 반당 학생 수", value: "oneToN" },
  { label: "1:1 — 학급마다 학생 1명", value: "oneToOne" },
];

const blankForm = () => ({
  schoolName: "",
  office: "",
  name: "",
  email: "",
  phonenum: "",
  grade: "3학년",
  publisher: "",
  mathPublisher: "",
  startDate: isoToDisplay(todayIso()),
  endDate: "",
  type: "-",
  issueMode: "default",
  classCnt: 1,
  studentCnt: 5,
});

const form = reactive(blankForm());
const startDateIso = ref(todayIso());
const endDateIso = ref("");
const isLowerGrade = computed(() => {
  const n = Number(String(form.grade).replace("학년", ""));
  return n === 1 || n === 2;
});
// 비상교과서는 5, 6학년 영어에만 있음
const BISANG_PUBLISHER = { label: "비상", value: "비상교과서" };
const englishPublisherItems = computed(() => {
  const n = Number(String(form.grade).replace("학년", ""));
  return n >= 5 ? [...publisherOptions, BISANG_PUBLISHER] : publisherOptions;
});
watch(englishPublisherItems, (items) => {
  if (form.publisher && !items.some((o) => o.value === form.publisher)) form.publisher = "";
});
const mathPublisherItems = computed(() =>
  isLowerGrade.value
    ? [MATH_LOWER_GRADE_PUBLISHER]
    : mathPublisherOptions.filter((p) => p !== MATH_LOWER_GRADE_PUBLISHER)
);
// 학년 변경 시 선택 불가능해진 값 정리
watch(isLowerGrade, (lower) => {
  if (lower) {
    form.publisher = "";
    // 1, 2학년은 천재교과서 하나뿐이라 자동 선택
    form.mathPublisher = MATH_LOWER_GRADE_PUBLISHER;
  } else if (form.mathPublisher === MATH_LOWER_GRADE_PUBLISHER) {
    form.mathPublisher = "";
  }
});
watch(startDateIso, (v) => {
  form.startDate = isoToDisplay(v);
});
watch(endDateIso, (v) => {
  form.endDate = isoToDisplay(v);
});
const submitting = ref(false);
const errorMsg = ref("");

// 모달 상태
const showConfirm = ref(false);
const showResult = ref(false);
const resultStatus = ref("ok"); // 'ok' | 'err'
const resultMessage = ref("");
const resultMeta = ref(null);

// 학급 수 상한 — 1:1 발급은 70반, 1:N은 20반 (백엔드 createGuest 클램프와 동일)
const maxClassCnt = computed(() => (form.issueMode === "oneToOne" ? 70 : 20));

// 발급 방식에 따른 학급/학생 수 계산
const resolvedCounts = computed(() => {
  if (form.issueMode === "oneToN") {
    return { classCnt: form.classCnt, studentCnt: form.studentCnt };
  }
  if (form.issueMode === "oneToOne") {
    return { classCnt: form.classCnt, studentCnt: 1 };
  }
  return { classCnt: 1, studentCnt: 5 };
});

const issueSummaryText = computed(() => {
  const { classCnt, studentCnt } = resolvedCounts.value;
  const c = Number(classCnt) || 0;
  const s = Number(studentCnt) || 0;
  const total = c * (s + 2); // 반당 교사 1 + LMS 1 + 학생 s
  return `학급 ${c}개 × (교사 1 + LMS 1 + 학생 ${s}명) = 총 ${total}개 계정`;
});

const studentRangeText = (cls) => {
  const ids = cls?.studentIds ?? [];
  if (ids.length === 0) return "-";
  if (ids.length === 1) return ids[0];
  return `${ids[0]} ~ ${ids[ids.length - 1]}`;
};

const isPositiveInt = (v, max) =>
  Number.isInteger(v) && v >= 1 && v <= max;

const validate = () => {
  if (!form.schoolName.trim()) return "학교명을 입력해주세요.";
  if (!form.name.trim()) return "담당 교사 이름을 입력해주세요.";
  if (!form.email.trim()) return "이메일을 입력해주세요.";
  if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) return "이메일 형식이 올바르지 않습니다.";
  if (!form.grade) return "학년을 선택해주세요.";
  if (isLowerGrade.value) {
    if (form.mathPublisher !== MATH_LOWER_GRADE_PUBLISHER) return "1, 2학년은 수학 출판사 '천재교과서'만 선택할 수 있습니다.";
  } else if (!form.publisher) return "영어 출판사를 선택해주세요.";
  if (!form.startDate.trim()) return "체험 시작일을 입력해주세요.";
  if (form.type === "-") return "분류를 선택해주세요.";
  if (
    form.issueMode !== "default" &&
    !isPositiveInt(form.classCnt, maxClassCnt.value)
  ) {
    return `학급 수는 1~${maxClassCnt.value} 사이의 정수로 입력해주세요.`;
  }
  if (form.issueMode === "oneToN" && !isPositiveInt(form.studentCnt, 70)) {
    return "반당 학생 수는 1~70 사이의 정수로 입력해주세요.";
  }
  return "";
};

const onSubmit = () => {
  errorMsg.value = "";
  const err = validate();
  if (err) {
    errorMsg.value = err;
    return;
  }
  const gradeNum = Number(String(form.grade).replace("학년", ""));
  // 대교·비상은 5, 6학년 단원만 존재 (title.json)
  const limited = GRADE_LIMITED_PUBLISHERS[form.publisher];
  if (limited && !limited.grades.includes(gradeNum)) {
    resultStatus.value = "err";
    resultMessage.value =
      `${limited.label} 출판사는 ${limited.grades.join(", ")}학년만 계정 발급이 가능합니다.\n학년을 변경하거나 다른 출판사를 선택해주세요.`;
    resultMeta.value = null;
    showResult.value = true;
    return;
  }
  showConfirm.value = true;
};

const onConfirmIssue = async () => {
  submitting.value = true;
  try {
    const { classCnt, studentCnt } = resolvedCounts.value;
    const payload = {
      idx: -Date.now(), // 수동 처리는 음수 timestamp 로 식별 (자동 시트 idx와 충돌 방지)
      schoolName: form.schoolName.trim(),
      office: form.office || "",
      grade: form.grade,
      publisher: isLowerGrade.value ? MATH_PUBLISHER_VALUE : form.publisher,
      // 3~6학년 수학 출판사(한글명). 백엔드가 schools.mathPublishers["학년-반"]에 저장하고 1·2학년은 학년으로 강제한다.
      mathPublisher: form.mathPublisher || "",
      type: form.type,
      startDate: form.startDate.trim(),
      endDate: form.endDate.trim(),
      name: form.name.trim(),
      email: form.email.trim(),
      phonenum: form.phonenum.trim(),
      classCnt,
      studentCnt,
    };
    const res = await store.dispatch("POST_CREATE_GUEST", payload);
    showConfirm.value = false;
    if (res?.result === true) {
      resultStatus.value = "ok";
      resultMessage.value = "무료체험 계정 발급 및 전송이 완료되었습니다.";
      // 백엔드가 반환한 발급 결과 (schoolCode + 반별 계정 목록)
      resultMeta.value =
        res.data && typeof res.data === "object" ? res.data : { ok: true };

      // 총괄 계정 이어서 발급 블록은 운영 결정(2026-10-06, 수동 발급의 총괄 토글 제거)으로
      // 삭제 — 토글 변수만 제거되고 이 블록이 남아 발급 성공 직후 ReferenceError로
      // "오류" 결과가 표시되던 회귀를 함께 정리 (qa-feedback-1020 검증에서 발견)
      showResult.value = true;
      onClear();
    } else {
      resultStatus.value = "err";
      resultMessage.value = "오류가 발생했습니다. 입력값을 다시 확인해주세요.";
      resultMeta.value = null;
      showResult.value = true;
    }
  } catch (e) {
    showConfirm.value = false;
    resultStatus.value = "err";
    resultMessage.value = `오류: ${e?.message ?? e}`;
    resultMeta.value = null;
    showResult.value = true;
  } finally {
    submitting.value = false;
  }
};

const onClear = () => {
  Object.assign(form, blankForm());
  startDateIso.value = todayIso();
  endDateIso.value = "";
  errorMsg.value = "";
};
</script>

<style scoped>
.page-wrap {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
}

.card-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
}
.card-header-actions {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--space-4);
}
@media (max-width: 640px) {
  .form-grid {
    grid-template-columns: 1fr;
  }
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  min-width: 0;
}

.master-account-header {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}
.master-account-title {
  margin-bottom: 0;
}
.master-account-checkbox {
  width: 16px;
  height: 16px;
  accent-color: var(--color-primary-600);
  cursor: pointer;
}

/* 항상 같은 줄에 나란히 표시되어야 하는 필드 쌍 (그리드 홀짝 순서와 무관하게 고정) */
.field-group--pair {
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--space-4);
}
@media (max-width: 640px) {
  .field-group--pair {
    grid-template-columns: 1fr;
  }
}

.field-label {
  font-size: var(--font-body-strong-size);
  font-weight: var(--font-body-strong-weight);
  color: var(--text-primary);
}

.field-required {
  margin-left: var(--space-1);
  font-size: var(--font-caption-size);
  color: var(--color-error-text);
  font-weight: 400;
}

.issue-summary {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: var(--space-3) 0 0;
  padding: var(--space-2) var(--space-3);
  background: var(--color-info-bg);
  color: var(--color-info-text);
  border: 1px solid var(--color-info-border);
  border-radius: var(--radius-md);
  font-size: var(--font-small-size);
}

.form-error {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: var(--space-4) 0 0;
  padding: var(--space-2) var(--space-3);
  background: var(--color-error-bg);
  color: var(--color-error-text);
  border: 1px solid var(--color-error-border);
  border-radius: var(--radius-md);
  font-size: var(--font-small-size);
}

/* === 커스텀 모달 === */
.modal-card {
  background: var(--bg-surface-raised);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  font-family: notosansregular;
}

.modal-card__header {
  padding: var(--space-4) var(--space-5);
  border-bottom: 1px solid var(--border-default);
  background: var(--bg-surface);
}

.modal-card__title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 700;
  color: var(--text-primary);
}

.modal-card__title-icon { color: var(--color-primary-600); }
.modal-card__title-icon.is-ok { color: var(--color-success-text, #16a34a); }
.modal-card__title-icon.is-err { color: var(--color-error-text); }

.modal-card__body {
  padding: var(--space-5);
  max-height: 60vh;
  overflow-y: auto;
}

.modal-card__lead {
  margin: 0 0 var(--space-4);
  font-size: 14px;
  color: var(--text-secondary);
  white-space: pre-line;
}
.modal-card__lead.is-ok { color: var(--text-primary); }
.modal-card__lead.is-err { color: var(--color-error-text); }

.modal-card__list {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-2);
  margin: 0;
}
.modal-card__row {
  display: grid;
  grid-template-columns: 110px 1fr;
  gap: var(--space-3);
  align-items: baseline;
  padding: var(--space-2) var(--space-3);
  background: var(--bg-surface);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
}
.modal-card__row dt {
  font-size: 12px;
  color: var(--text-tertiary);
  font-weight: 600;
}
.modal-card__row dd {
  margin: 0;
  font-size: 13.5px;
  color: var(--text-primary);
  word-break: break-all;
}
.modal-card__footer {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-5);
  border-top: 1px solid var(--border-default);
  background: var(--bg-surface);
}

.modal-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 36px;
  padding: 0 var(--space-4);
  border-radius: var(--radius-md);
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, opacity 0.15s ease;
  border: 1px solid transparent;
}
.modal-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.modal-btn--primary {
  background: var(--color-primary-600);
  color: var(--text-on-primary);
}
.modal-btn--primary:hover:not(:disabled) {
  background: var(--color-primary-700, #3b73d8);
}
.modal-btn--secondary {
  background: var(--bg-surface-raised);
  color: var(--text-primary);
  border-color: var(--border-default);
}
.modal-btn--secondary:hover:not(:disabled) {
  background: var(--bg-muted);
}

.spin {
  animation: modalSpin 0.9s linear infinite;
}
@keyframes modalSpin {
  to { transform: rotate(360deg); }
}

@media (max-width: 640px) {
  .modal-card__row {
    grid-template-columns: 90px 1fr;
  }
  .modal-card__body { padding: var(--space-4); }
  .modal-card__header,
  .modal-card__footer { padding-left: var(--space-4); padding-right: var(--space-4); }
}
</style>
